/**
 * usePremium Hook
 *
 * Provides subscription tier information via Inference API GET /auth/me.
 * Effective tier: guest | free | homeowner_wealth | buyer_3month | pro_monthly.
 *
 * Tier model:
 *   guest            = not logged in — predicted price, confidence badge, over/under
 *   free             = logged in, no subscription — same as guest + relist insights
 *   homeowner_wealth = $19.99/year — full report on own home, unlimited ROI, 1 PDF/yr
 *   buyer_3month     = $79/3 months — full analysis + 20 predictions/day + 20 ROI/day + 10 PDFs
 *   pro_monthly      = $59/month — full analysis + 100 predictions/day + 100 ROI/day + 20 PDFs/mo
 *
 * Key booleans for gating:
 *   showBadges        — true for free + paid (anyone logged in)
 *   showFullAnalysis   — true for paid (buyer/pro) only
 *   isHomeownerWealth — true for homeowner_wealth tier
 *   canUseROI         — true for homeowner_wealth + buyer + pro
 *   canExportPDF      — true if user has any PDF credits remaining
 *
 * Caches effectiveTier in sessionStorage so navigation shows tier immediately.
 */

import { useState, useEffect } from 'react'
import { useAuth } from './useAuth'
import { getAccessToken, signOut } from '../services/auth'

import { API_BASE_URL, PAUSE_SUBSCRIPTIONS } from '../config'

const AUTH_ME_CACHE_TTL_MS = 60 * 1000
/** Abort hung /auth/me so UI (e.g. My Home) does not spin forever when API is unreachable. */
const AUTH_ME_FETCH_TIMEOUT_MS = 15_000
let _authMePromise = null
let _authMeCache = null
let _authMeCacheExpiry = 0
let _authMeCacheToken = null

const TIER_CACHE_KEY = 'propdna_tier_cache'
const TIER_CACHE_TTL_MS = 5 * 60 * 1000

function getCachedTier() {
  try {
    const raw = sessionStorage.getItem(TIER_CACHE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || !data.effectiveTier || (data.expiresAt && Date.now() > data.expiresAt)) return null
    return { 
      effectiveTier: data.effectiveTier, 
      homeownerProperty: data.homeownerProperty || null,
      savedSearches: data.savedSearches || [],
      pdfCreditsRemaining: data.pdfCreditsRemaining ?? null,
      predictionCreditsRemaining: data.predictionCreditsRemaining ?? null,
      unlockedProperties: data.unlockedProperties || [],
    }
  } catch {
    return null
  }
}

function setCachedTier(effectiveTier, homeownerProp, savedSearches, pdfCreditsRemaining, predictionCreditsRemaining, unlockedProperties) {
  try {
    sessionStorage.setItem(TIER_CACHE_KEY, JSON.stringify({
      effectiveTier,
      homeownerProperty: homeownerProp || null,
      savedSearches: savedSearches || [],
      pdfCreditsRemaining: pdfCreditsRemaining ?? null,
      predictionCreditsRemaining: predictionCreditsRemaining ?? null,
      unlockedProperties: unlockedProperties || [],
      expiresAt: Date.now() + TIER_CACHE_TTL_MS,
    }))
  } catch (_) {}
}

function clearTierCache() {
  try {
    sessionStorage.removeItem(TIER_CACHE_KEY)
  } catch (_) {}
}

export function getCacheTierForBrowse() {
  const c = getCachedTier()
  return c?.effectiveTier ?? 'guest'
}

function fetchAuthMeCached(token) {
  const now = Date.now()
  if (_authMeCache && _authMeCacheToken === token && now < _authMeCacheExpiry) {
    return Promise.resolve(_authMeCache)
  }
  if (_authMePromise) {
    return _authMePromise.then((me) => {
      if (me && _authMeCacheToken === token) return me
      return _authMeCache
    })
  }
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), AUTH_ME_FETCH_TIMEOUT_MS)
  _authMePromise = fetch(`${API_BASE_URL}/api/v1/auth/me`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    signal: controller.signal,
  }).then(async (r) => {
    clearTimeout(timeoutId)
    const me = r.ok ? await r.json() : null
    _authMePromise = null
    if (r.status === 403) {
      clearAuthMeCache()
      signOut()
      return null
    }
    if (me) {
      _authMeCache = me
      _authMeCacheExpiry = now + AUTH_ME_CACHE_TTL_MS
      _authMeCacheToken = token
    }
    return me
  }).catch((err) => {
    clearTimeout(timeoutId)
    _authMePromise = null
    throw err
  })
  return _authMePromise
}

export function clearAuthMeCache() {
  _authMePromise = null
  _authMeCache = null
  _authMeCacheExpiry = 0
  _authMeCacheToken = null
  clearTierCache()
}

export const TIERS = {
  GUEST: 'guest',
  FREE: 'free',
  HOMEOWNER_WEALTH: 'homeowner_wealth',
  BUYER_3MONTH: 'buyer_3month',
  PRO_MONTHLY: 'pro_monthly',
  // Legacy aliases
  BUYER_SELLER: 'buyer_seller',
  INVESTOR: 'investor',
}

export const TIER_LIMITS = {
  guest:            { customPredictionsPerDay: 0, roiSimsPerDay: 0,      savedSearches: 0,  savedPredictions: 0,   pdfExportsIncluded: 0  },
  // free.roiSimsPerDay = 10 is only honored when PAUSE_SUBSCRIPTIONS is on (backend enforces).
  // When pause is off, canUseROI returns false for free users so this value isn't user-facing.
  free:             { customPredictionsPerDay: 0, roiSimsPerDay: 10,     savedSearches: 10, savedPredictions: 0,   pdfExportsIncluded: 0  },
  homeowner_wealth: { customPredictionsPerDay: 0, roiSimsPerDay: 999999, savedSearches: 10, savedPredictions: 0,   pdfExportsIncluded: 1  },
  buyer_3month:     { customPredictionsPerDay: 20, roiSimsPerDay: 20,    savedSearches: 5,  savedPredictions: 20,  pdfExportsIncluded: 10 },
  pro_monthly:      { customPredictionsPerDay: 100, roiSimsPerDay: 100,  savedSearches: 15, savedPredictions: 100, pdfExportsIncluded: 20 },
  // Legacy
  buyer_seller:     { customPredictionsPerDay: 20, roiSimsPerDay: 20,    savedSearches: 5,  savedPredictions: 20,  pdfExportsIncluded: 10 },
  investor:         { customPredictionsPerDay: 100, roiSimsPerDay: 100,  savedSearches: 15, savedPredictions: 100, pdfExportsIncluded: 20 },
}

function tierFromEffective(eff) {
  if (eff === 'pro_monthly' || eff === 'investor') return TIERS.PRO_MONTHLY
  if (eff === 'buyer_3month' || eff === 'buyer_seller') return TIERS.BUYER_3MONTH
  if (eff === 'homeowner_wealth') return TIERS.HOMEOWNER_WEALTH
  if (eff === 'guest') return TIERS.GUEST
  return TIERS.FREE
}

function getInitialTierState() {
  const cached = getCachedTier()
  if (cached) {
    return {
      tier: tierFromEffective(cached.effectiveTier),
      effectiveTier: cached.effectiveTier,
      savedSearches: cached.savedSearches || [],
    }
  }
  
  // Fallback to stored session if no tier cache yet (e.g. just signed in)
  try {
    const raw = localStorage.getItem('propdna_auth')
    if (raw) {
      const data = JSON.parse(raw)
      if (data.effectiveTier) {
        return {
          tier: tierFromEffective(data.effectiveTier),
          effectiveTier: data.effectiveTier,
          savedSearches: [],
        }
      }
    }
  } catch (_) {}

  return { tier: TIERS.FREE, effectiveTier: 'free', savedSearches: [] }
}

export const usePremium = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const [tier, setTier] = useState(() => getInitialTierState().tier)
  const [effectiveTier, setEffectiveTier] = useState(() => getInitialTierState().effectiveTier)
  const [loading, setLoading] = useState(true)
  const [meResolved, setMeResolved] = useState(false)  // true only after /auth/me has fully responded
  const [error, setError] = useState(null)
  const [homeownerProperty, setHomeownerProperty] = useState(null)
  const [savedSearches, setSavedSearches] = useState(() => getInitialTierState().savedSearches)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [usageCounters, setUsageCounters] = useState(() => {
    const cached = getCachedTier()
    return {
      customPredictionsToday: 0,
      customPredictionsLimit: 0,
      roiSimulationsToday: 0,
      roiSimulationsLimit: 0,
      pdfCreditsRemaining: cached?.pdfCreditsRemaining ?? 0,
      predictionCreditsRemaining: cached?.predictionCreditsRemaining ?? 0,
    }
  })
  const [usageLoaded, setUsageLoaded] = useState(false)
  const [cacheVersion, setCacheVersion] = useState(0)
  const [unlockedProperties, setUnlockedProperties] = useState(() => getCachedTier()?.unlockedProperties || [])

  useEffect(() => {
    const fetchMe = async () => {
      setError(null)

      if (authLoading) return

      if (!isAuthenticated || !user) {
        clearAuthMeCache()
        setTier(TIERS.GUEST)
        setEffectiveTier('guest')
        setHomeownerProperty(null)
        setSavedSearches([])
        setUnlockedProperties([])
        setUsageCounters({
          customPredictionsToday: 0,
          customPredictionsLimit: 0,
          roiSimulationsToday: 0,
          roiSimulationsLimit: 0,
          pdfCreditsRemaining: 0,
          predictionCreditsRemaining: 0,
        })
        setLoading(false)
        setUsageLoaded(true)
        setMeResolved(true)
        return
      }

      const token = getAccessToken()
      if (!token) {
        setTier(TIERS.FREE)
        setEffectiveTier('free')
        setHomeownerProperty(null)
        setSavedSearches([])
        setUnlockedProperties([])
        setUsageCounters({
          customPredictionsToday: 0,
          customPredictionsLimit: 0,
          roiSimulationsToday: 0,
          roiSimulationsLimit: 0,
          pdfCreditsRemaining: 0,
          predictionCreditsRemaining: 0,
        })
        setLoading(false)
        setUsageLoaded(true)
        setMeResolved(true)
        return
      }

      const cached = getCachedTier()
      if (cached) {
        setEffectiveTier(cached.effectiveTier)
        setTier(tierFromEffective(cached.effectiveTier))
        if (cached.homeownerProperty) setHomeownerProperty(cached.homeownerProperty)
        if (cached.savedSearches) setSavedSearches(cached.savedSearches)
        if (cached.unlockedProperties?.length) setUnlockedProperties(cached.unlockedProperties)
        if (cached.pdfCreditsRemaining !== null) {
          setUsageCounters((prev) => ({ ...prev, pdfCreditsRemaining: cached.pdfCreditsRemaining }))
        }
        if (cached.predictionCreditsRemaining !== null) {
          setUsageCounters((prev) => ({ ...prev, predictionCreditsRemaining: cached.predictionCreditsRemaining }))
        }
        setUsageLoaded(true)
        setLoading(false)
      } else {
        // Optimistic update: use effectiveTier stored in localStorage by signIn/signUp
        // so the UI reflects the correct tier immediately without waiting for /auth/me
        try {
          const stored = JSON.parse(localStorage.getItem('propdna_auth') || '{}')
          if (stored.effectiveTier) {
            setEffectiveTier(stored.effectiveTier)
            setTier(tierFromEffective(stored.effectiveTier))
            setLoading(false)
          } else {
            setLoading(true)
          }
        } catch {
          setLoading(true)
        }
      }

      try {
        const me = await fetchAuthMeCached(token)
        if (!me) {
          const fallback = getCachedTier()
          if (!fallback) {
            setTier(TIERS.FREE)
            setEffectiveTier('free')
          }
          setUsageLoaded(true)
          setLoading(false)
          return
        }
        const eff = me.effectiveTier || 'free'
        setEffectiveTier(eff)
        setTier(tierFromEffective(eff))
        const homeProp = me.homeowner_property || null
        const saved = me.saved_searches || []
        const pdfCredits = me.pdf_credits_remaining ?? 0
        const predictionCredits = me.prediction_credits_remaining ?? 0
        const unlocked = me.unlocked_properties || []
        setCachedTier(eff, homeProp, saved, pdfCredits, predictionCredits, unlocked)
        setHomeownerProperty(homeProp)
        setSavedSearches(saved)
        setUnlockedProperties(unlocked)

        setUsageCounters({
          customPredictionsToday: me.custom_predictions_today ?? 0,
          customPredictionsLimit: me.custom_predictions_limit ?? 0,
          roiSimulationsToday: me.roi_simulations_today ?? 0,
          roiSimulationsLimit: me.roi_simulations_limit ?? 0,
          pdfCreditsRemaining: pdfCredits,
          predictionCreditsRemaining: predictionCredits,
        })
        setUsageLoaded(true)
        setCacheVersion((v) => v + 1)
      } catch (err) {
        console.error('Error fetching /auth/me:', err)
        setError(err.message)
        const fallback = getCachedTier()
        if (!fallback) {
          setTier(TIERS.FREE)
          setEffectiveTier('free')
        }
        setUsageLoaded(true)
      } finally {
        setLoading(false)
        setMeResolved(true)
      }
    }

    fetchMe()
  }, [user, isAuthenticated, authLoading, refreshVersion])

  const limits = TIER_LIMITS[effectiveTier] || TIER_LIMITS.free

  // Core tier booleans
  const isGuest = effectiveTier === 'guest'
  const isFree = effectiveTier === 'free'
  const isHomeownerWealth = effectiveTier === 'homeowner_wealth'
  const isBuyer = effectiveTier === 'buyer_3month' || effectiveTier === 'buyer_seller'
  const isPro = effectiveTier === 'pro_monthly' || effectiveTier === 'investor'
  const isPremium = isBuyer || isPro

  // Feature-level gating
  const showBadges = true           // Everyone (Ghost, Free, Paid) sees the existence of badges
  // When subscriptions are paused, guests get elevated badge visibility (former Free tier).
  const showBadgeDetails = !isGuest || PAUSE_SUBSCRIPTIONS
  const showFullAnalysis = isPremium // Paid only: exact percentages, intervals, adjusted comps, DNA details
  // Under PAUSE_SUBSCRIPTIONS: ROI unlocked for any signed-in user (lead-gen mode).
  // Guests still hit the sign-in wall so we capture an email.
  const canUseROI = isHomeownerWealth || isPremium || (PAUSE_SUBSCRIPTIONS && !isGuest)
  const canExportPDF = usageCounters.pdfCreditsRemaining > 0 || (PAUSE_SUBSCRIPTIONS && !isGuest)

  // Legacy aliases
  const isBuyerSeller = isBuyer
  const isInvestor = isPro
  const isTrial = false

  const tierDisplayName = isGuest ? 'Guest'
    : isFree ? 'Free'
    : isHomeownerWealth ? 'Home Wealth'
    : isBuyer ? 'Buyer'
    : isPro ? 'Pro'
    : 'Free'

  const refreshCredits = () => {
    clearAuthMeCache()
    setRefreshVersion((v) => v + 1)
  }

  /**
   * Check if a specific property has been unlocked via Quick Unlock ($7.99).
   * @param {string} propertySlug - The URL slug for the property (e.g. "123-main-st_toronto")
   * @returns {boolean}
   */
  const isPropertyUnlocked = (propertySlug) => {
    if (!propertySlug) return false
    const key = String(propertySlug).toLowerCase()
    return (unlockedProperties || []).some((item) => {
      const id = typeof item === 'object' ? item?.property_id : item
      return String(id || '').toLowerCase() === key
    })
  }

  return {
    tier,
    effectiveTier,
    tierDisplayName,
    isGuest,
    isFree,
    isHomeownerWealth,
    isBuyer,
    isPro,
    isPremium,
    isTrial,
    isBuyerSeller,
    isInvestor,
    showBadges,
    showBadgeDetails,
    showFullAnalysis,
    canUseROI,
    canExportPDF,
    customPredictionsLimit: limits.customPredictionsPerDay,
    savedSearchesLimit: limits.savedSearches,
    savedPredictionsLimit: limits.savedPredictions,
    limits,
    usageCounters,
    loading,
    meResolved,
    usageLoaded,
    error,
    homeownerProperty,
    savedSearches,
    cacheVersion,
    refreshCredits,
    isPropertyUnlocked,
    unlockedProperties,
  }
}
